import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { AppDispatch, RootState } from "@/app/store";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { colocaToast } from "@/shared/utils/toast";

import { resolveNotificationSource } from "../config/notification-sources.config";
import { notificationsSocketService } from "../services/notifications-socket.service";
import { DEFAULT_NOTIFICATIONS_LIMIT } from "../services/notifications.service";
import {
  fetchNotifications,
  pushNotification,
  setConnectionStatus,
  setNotificationRead,
  resetNotificationsState,
} from "../store/notificationsSlice";
import { startNotificationSoundLoop, stopNotificationSoundLoop } from "../utils/playNotificationSound";

export function useNotifications() {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();
  const source = user ? resolveNotificationSource(user.user_type) : null;
  const enabled = !!source?.enabled;
  const restListEndpoint = source?.restListEndpoint ?? null;

  const { items, total, hasMore, isLoading, error, connectionStatus } = useSelector(
    (state: RootState) => state.notifications,
  );

  const unreadCount = items.filter((n) => !n.read_at).length;

  const loadMore = useCallback(() => {
    if (!enabled || !restListEndpoint) return;
    dispatch(
      fetchNotifications({
        skip: items.length,
        limit: DEFAULT_NOTIFICATIONS_LIMIT,
        endpoint: restListEndpoint,
      }),
    );
  }, [dispatch, items.length, enabled, restListEndpoint]);

  const loadList = useCallback(() => {
    if (!enabled || !restListEndpoint) return;
    dispatch(
      fetchNotifications({
        skip: 0,
        limit: DEFAULT_NOTIFICATIONS_LIMIT,
        endpoint: restListEndpoint,
      }),
    );
  }, [dispatch, enabled, restListEndpoint]);

  const markAsRead = useCallback(
    (id: string) => {
      if (!enabled) return;
      notificationsSocketService.markAsRead(id);
    },
    [enabled],
  );

  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = notificationsSocketService.onNotification((notification, eventName) => {
      if (eventName === "notification:created") {
        dispatch(pushNotification(notification));
      }
    });
    return unsubscribe;
  }, [dispatch, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = notificationsSocketService.onMarkReadOk((notificationId) => {
      dispatch(setNotificationRead(notificationId));
    });
    return unsubscribe;
  }, [dispatch, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = notificationsSocketService.onError((message) => {
      colocaToast.error(message);
    });
    return unsubscribe;
  }, [enabled]);

  return {
    items,
    total,
    hasMore,
    isLoading,
    error,
    connectionStatus,
    unreadCount,
    loadList,
    loadMore,
    markAsRead,
  };
}

export function useNotificationSoundLoop(enabled: boolean = true): void {
  const unreadCount = useSelector(
    (state: RootState) => state.notifications.items.filter((n) => !n.read_at).length,
  );

  useEffect(() => {
    if (enabled && unreadCount > 0) {
      startNotificationSoundLoop();
    } else {
      stopNotificationSoundLoop();
    }
    return () => {
      stopNotificationSoundLoop();
    };
  }, [enabled, unreadCount]);
}

export function useNotificationsSocket(isAuthenticated: boolean) {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();

  useEffect(() => {
    notificationsSocketService.setCurrentUser(user ? { user_type: user.user_type, role: user.role } : null);
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) {
      notificationsSocketService.disconnect();
      notificationsSocketService.setOnStatusChange(null);
      dispatch(setConnectionStatus("disconnected"));
      dispatch(resetNotificationsState());
      return;
    }

    notificationsSocketService.setOnStatusChange((status) => {
      dispatch(setConnectionStatus(status));
    });

    dispatch(setConnectionStatus("connecting"));
    notificationsSocketService.connect();

    return () => {
      notificationsSocketService.disconnect();
      notificationsSocketService.setOnStatusChange(null);
      dispatch(setConnectionStatus("disconnected"));
    };
  }, [isAuthenticated, dispatch]);
}
